import React, { useState, useEffect, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import cn from 'classnames'

import styles from './RichCheckout.module.scss'
import { RICH_CHECKOUT_TEXT_PROMPT, TEAM_LOGO_EXISTS } from '../constants'
import GratuityHeader from '../components/GratuityHeader'
import BackButton from '../components/BackButton'
import QRCode from '../components/QRCode'
import { makeGetOrder } from '../selectors/order'
import { richCheckoutOrderCompleted } from '../actions/richCheckout/order'
import { nextPosOrderNumber } from '../utils/orderNumbers'
import useTimeout from '../hooks/useTimeout'
import { makeGetRemoteOrderTotal } from '../selectors/orderTotalRemote'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { generateURL } from '../utils/richCheckout'
import { filter, find, once, map, orderBy, reduce } from 'lodash'
import generateUuid from 'uuid/v4'
import { ReactComponent as CheckMarkIcon } from '../assets/icons/checkmark-icon.svg'
import Button from '../components/Button'

import { VNPusher as VNPusherPrototype } from '../pusher'
import { orderDetailsRequested } from '../actions/order'

import { ReactComponent as MobileIcon } from '../assets/icons/mobileIcon.svg'
import { ReactComponent as MobileIcon2 } from '../assets/icons/mobileIcon2.svg'
import Dialog from '../components/dialog/Dialog'
import { toast } from 'react-toastify'
import useNetworkIsAvailable from '../hooks/useNetworkIsAvailable'

const ScannedIcon = ({ className }) => {
  return (
    <div className={cn(className, styles.scannedIcon)}>
      <div className={styles.circle}>
        <CheckMarkIcon className={styles.check} />
      </div>
    </div>
  )
}

const RichCheckout = ({ isNewOrder, orderId, deviceId, totalInCents, items, orderNumber, totalDisplay, subtotalDisplay, saveRichCheckoutOrder, orderDidComplete, goBack, className }) => {
  const classNames = cn(styles.richCheckout, className)
  const VNPusher = useRef()
  const [qrCodeData, setQRCodeData] = useState(generateURL(orderId, orderNumber, deviceId, totalInCents, items))
  const [isScanned, setIsScanned] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [resolvedOrderNumber] = useState(orderNumber ?? once(nextPosOrderNumber))
  const [promptHeader, setPromptHeader] = useState('Pay With App')
  const [promptMessage, setPromptMessage] = useState(RICH_CHECKOUT_TEXT_PROMPT)
  const isMobileViewPort = document?.body?.clientWidth < 400
  const [confirmWithoutSignal, setConfirmWithoutSignal] = useState(false)
  const [networkIsAvailable] = useNetworkIsAvailable()

  useEffect(() => {
    VNPusher.current = new VNPusherPrototype()
  }, [])

  useEffect(() => {
    if (!networkIsAvailable) {
      setTimeout(() => {
        setConfirmWithoutSignal(true)
      }, 45000)
    }
}, [networkIsAvailable])

  useEffect(() => {
    if (isScanned) {
      setPromptHeader('Processing...')
      setPromptMessage('please wait')
    } else {
      setPromptHeader('Pay With App')
      setPromptMessage(RICH_CHECKOUT_TEXT_PROMPT)
    }
  }, [isScanned])

  useEffect(() => {
    if (isSuccess) {
      setPromptHeader('Success!')
    } else {
      setPromptHeader('Pay With App')
      setPromptMessage(RICH_CHECKOUT_TEXT_PROMPT)
    }
  }, [isSuccess])

  useDeepCompareEffect(() => {
    setQRCodeData(generateURL(orderId, resolvedOrderNumber, deviceId, totalInCents, items))
  }, [orderId, orderNumber, deviceId, totalInCents, items])

  const onBind = () => {
    setIsScanned(true)
  }

  const onOrderCreated = () => {
    setIsSuccess(true)
    onConfirmOrder()
  }

  const onConfirmOrder = () => {
    if (!orderId) {
      toast.error('Could not confirm order, no order ID')
      goBack()
      return
    }
    saveRichCheckoutOrder({ orderId, settledOrderNumber: resolvedOrderNumber })
  }

  // pusher socket for rich checkout
  useEffect(() => {
    VNPusher.current.subscribeDeviceChannel()
    VNPusher.current.bindOrderTransfered(onBind)
    VNPusher.current.bindOrderCreated(onOrderCreated)

    // we'll want to use the below when the ios sdk persists the order uuid from the qr code
    // const orderChannel = socket.subscribe(`private-order-${uuid}`)
    // orderChannel.bind('order-created', (data) => {
    //   saveRichCheckoutOrder({ uuid, orderNumber })
    //   history.replace(`/rich-checkout/receipt/${uuid}`)
    // })
    return () => {
      VNPusher.current.unsubscribeDeviceChannel()
    }
  // TODO: We need to audit the exhaustive dependencies here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let dialogModal = null
  if (isScanned && !isSuccess) {
    dialogModal = (
       <Dialog >
            <div className={styles.isScannedModal}>
              <MobileIcon className={styles.icon}/>
              <div className={styles.textBox}>Please finish this transaction on your phone.</div>
              <div className={styles.buttonContainer} onClick={goBack} style={{justifyContent: 'center'}}>
                GO BACK
              </div>
            </div>
        </Dialog>
    )
  }

  if (confirmWithoutSignal) {
    dialogModal = (
       <Dialog >
            <div className={styles.isScannedModal}>
              <MobileIcon2 className={styles.icon}/>
              <div className={styles.textBoxSuccess}>Please show your mobile receipt to the attendant to confirm your order.</div>
              <div className={styles.buttonContainer}>
                <div className={styles.button} onClick={goBack}>
                  GO BACK
                </div>
                <div className={styles.button} onClick={onConfirmOrder}>
                  CONFIRM ORDER
                </div>
              </div>
            </div>
        </Dialog>
    )
  }

  return (
    <div className={classNames}>
      <div className={styles.header}>
        <div className={styles.backButton}>
        <BackButton onClick={goBack} />
        {isMobileViewPort && <div className={styles.border} />}
        </div>
        <GratuityHeader
          className={cn(styles.gratuityHeader)}
          primaryText={totalDisplay}
        />
      </div>
      <div className={cn(styles.qrCodeContainer, { [styles.scanned]: isScanned })}>
        <div>
          {!isScanned && <QRCode className={styles.qrCode} data={qrCodeData} size={isMobileViewPort ? 164 : 230} />}
        </div>
      </div>
      <div className={styles.footer}>
      {TEAM_LOGO_EXISTS && (
          <img className={styles.teamLogo} src="/teamLogo.png" />
        )}
        <p>{promptHeader}</p>
        <p>{promptMessage}</p>
      </div>
      {dialogModal}
    </div>
  )
}

const formatRemoteItems = (items) => {
  const sorted = orderBy(items, 'parentLineItemUuid', 'desc')
  const purged = filter(sorted, ['specialType', null])

  const formattedResult = reduce(purged, (result, item) => {
    if (item.parentLineItemUuid) {
      const parentItem = find(result, ['_lineItemId', item.parentLineItemUuid])

      parentItem.modifiers = [
        ...(parentItem.modifiers ?? []),
        {
          id: item.menuItemUuid,
          quantity: 1,
        }
      ]

      return result
    }

    return [
      ...result,
      {
        id: item.menuItemUuid,
        _lineItemId: item.lineItemUuid,
        modifiers: [],
        quantity: item.quantity,
      }
    ]
  }, [])

  const parsedResult = map(formattedResult, (item) => {
    const { _lineItemId, ...rest } = item

    return rest
  })

  return parsedResult
}

const formatLocalItems = (items) => {
  return map(items, (item) => {
    return {
      id: item.id,
      quantity: 1,
      modifiers: map(item.modifiers, (modifier) => {
        return {
          id: modifier.id,
          quantity: 1,
        }
      }),
    }
  })
}

const mapStateToProps = (state, props) => {
  const getOrder = makeGetOrder()
  const selectRemoteOrderTotal = makeGetRemoteOrderTotal()

  const deviceId = state?.config?.uuid ?? localStorage.getItem('device_id')
  const orderIdParam = props?.match?.params?.orderId
  const isNewOrder = orderIdParam === undefined
  let orderId = orderIdParam

  const order = getOrder(state, orderId)
  const remoteOrderTotal = selectRemoteOrderTotal(state)
  let orderNumber = order?.orderNumber
  let totalDisplay = order?.orderTotalFromOrder?.totalDisplay
  let subtotalDisplay = order?.orderTotalFromOrder?.totalDisplay
  let totalInCents = order?.orderTotalFromOrder?.totalAmountInCents
  let items = formatLocalItems(order?.itemModels)

  if (isNewOrder) {
    totalDisplay = remoteOrderTotal?.totalDisplay
    subtotalDisplay = remoteOrderTotal?.subtotalDisplay
    totalInCents = remoteOrderTotal?.totalAmountInCents
    items = formatRemoteItems(remoteOrderTotal?.lineItems)
    orderId = remoteOrderTotal?.uuid
  }

  return {
    items,
    orderId,
    deviceId,
    isNewOrder,
    orderNumber,
    totalInCents,
    totalDisplay,
    subtotalDisplay,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const userNotes = props?.location?.state?.userNotes ?? null
  const goBack = props?.history?.goBack ?? function(){}
  const replace = props?.history?.replace ?? function(){}
  const orderIdParam = props?.match?.params?.orderId
  const isNewOrder = orderIdParam === undefined

  return {
    goBack: () => {
      if (isNewOrder) {
        goBack()
      } else {
        replace('/tabs')
      }
    },
    saveRichCheckoutOrder: (data) => {
      const payload = data

      if (userNotes) payload.userNotes = userNotes

      dispatch(richCheckoutOrderCompleted(payload))
    },
    orderDidComplete: (orderId) => {
      dispatch(orderDetailsRequested(orderId))
      replace(`/tabs/${orderId}`)
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RichCheckout)
